import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  BriefcaseBusiness,
  MapPin,
  Search,
  SlidersHorizontal,
  Wallet,
  Clock,
  UserRound,
  Phone,
  MessageCircle,
  Mail
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import API from "../../../api";
import "./find.work.css";

export default function FindWork() {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const [workPosts, setWorkPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showConsult, setShowConsult] = useState(false);
const [consultUser, setConsultUser] = useState(null);


  /* =====================================================
     LOAD REAL WORK POSTS
  ===================================================== */

  useEffect(() => {
    const loadWorkPosts = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await API.get(
          "/work-posts"
        );

        if (res.data.success) {
          setWorkPosts(
            res.data.workPosts || []
          );
        } else {
          setError(
            res.data.message ||
            "Unable to load work posts."
          );
        }

      } catch (err) {
        console.error(
          "Find work load failed:",
          err
        );

        setError(
          err.response?.data?.message ||
          "Unable to load work posts."
        );

      } finally {
        setLoading(false);
      }
    };

    loadWorkPosts();
  }, []);

  const openContactProfessional = (professional) => {
  if (!localStorage.getItem("token")) {
    navigate("/login");
    return;
  }

  setConsultUser(professional);
  setShowConsult(true);
};


  /* =====================================================
     FILTER WORK POSTS
  ===================================================== */

  const filteredWorkPosts = useMemo(() => {
    const query =
      search.trim().toLowerCase();

    const currentUser =
      JSON.parse(
        localStorage.getItem(
          "civilink_user"
        ) || "null"
      );

    const userCity =
      currentUser?.location?.city
        ?.trim()
        .toLowerCase() || "";

    const userSkills =
      Array.isArray(
        currentUser?.skills
      )
        ? currentUser.skills.map(
            (skill) =>
              String(skill)
                .trim()
                .toLowerCase()
          )
        : [];

    return workPosts.filter((work) => {

      const title =
        String(
          work.title || ""
        ).toLowerCase();

      const description =
        String(
          work.description || ""
        ).toLowerCase();

      const city =
        String(
          work.location?.city || ""
        ).toLowerCase();

      const state =
        String(
          work.location?.state || ""
        ).toLowerCase();

      const workType =
        String(
          work.workType || ""
        ).toLowerCase();

      const skills =
        Array.isArray(work.skills)
          ? work.skills.map(
              (skill) =>
                String(skill)
                  .trim()
                  .toLowerCase()
            )
          : [];


      /* =========================================
         SEARCH
      ========================================= */

      const matchesSearch =
        !query ||
        title.includes(query) ||
        description.includes(query) ||
        city.includes(query) ||
        state.includes(query) ||
        workType.includes(query) ||
        skills.some(
          (skill) =>
            skill.includes(query)
        );


      /* =========================================
         ALL
      ========================================= */

      if (filter === "all") {
        return matchesSearch;
      }


      /* =========================================
         NEARBY
      ========================================= */

      if (filter === "nearby") {
        return (
          matchesSearch &&
          userCity &&
          city === userCity
        );
      }


      /* =========================================
         MY SKILLS
      ========================================= */

      if (filter === "skills") {

        if (!userSkills.length) {
          return false;
        }

        const skillMatch =
          skills.some(
            (workSkill) =>
              userSkills.includes(
                workSkill
              )
          );

        return (
          matchesSearch &&
          skillMatch
        );
      }


      return matchesSearch;
    });

  }, [workPosts, search, filter]);


  /* =====================================================
     DATE FORMAT
  ===================================================== */

  const formatPostedDate = (
    date
  ) => {

    if (!date) {
      return "";
    }

    const created =
      new Date(date);

    if (
      Number.isNaN(
        created.getTime()
      )
    ) {
      return "";
    }

    const difference =
      Date.now() -
      created.getTime();

    const minutes =
      Math.floor(
        difference / 60000
      );

    if (minutes < 60) {
      return `${Math.max(
        1,
        minutes
      )}m ago`;
    }

    const hours =
      Math.floor(
        minutes / 60
      );

    if (hours < 24) {
      return `${hours}h ago`;
    }

    const days =
      Math.floor(
        hours / 24
      );

    if (days < 7) {
      return `${days}d ago`;
    }

    return created.toLocaleDateString(
      "en-IN"
    );
  };


  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {
    return (
      <div className="find-work-page">

        <header className="find-work-header">

          <button
            type="button"
            className="find-work-back"
            onClick={() =>
              navigate(-1)
            }
          >
            <ArrowLeft size={19} />
          </button>

          <div>

            <span className="find-work-eyebrow">
              CIVILINK OPPORTUNITIES
            </span>

            <h1>
              Find Work
            </h1>

          </div>

        </header>


        <div className="find-work-loading">

          <div className="work-loading-card" />
          <div className="work-loading-card" />
          <div className="work-loading-card" />

        </div>

      </div>
    );
  }


  /* =====================================================
     ERROR
  ===================================================== */

  if (error) {
    return (
      <div className="find-work-page">

        <header className="find-work-header">

          <button
            type="button"
            className="find-work-back"
            onClick={() =>
              navigate(-1)
            }
          >
            <ArrowLeft size={19} />
          </button>

          <div>

            <span className="find-work-eyebrow">
              CIVILINK OPPORTUNITIES
            </span>

            <h1>
              Find Work
            </h1>

          </div>

        </header>


        <div className="find-work-empty">

          <BriefcaseBusiness size={28} />

          <strong>
            Unable to load work
          </strong>

          <p>
            {error}
          </p>

          <button
            type="button"
            className="work-view-btn"
            onClick={() =>
              window.location.reload()
            }
          >
            Try again
          </button>

        </div>

      </div>
    );
  }


  /* =====================================================
     MAIN
  ===================================================== */

  return (
    <div className="find-work-page">

      {/* =================================================
          HEADER
      ================================================= */}

      <header className="find-work-header">

        <button
          type="button"
          className="find-work-back"
          onClick={() =>
            navigate(-1)
          }
          aria-label="Go back"
        >
          <ArrowLeft size={19} />
        </button>


        <div>

          <span className="find-work-eyebrow">
            CIVILINK OPPORTUNITIES
          </span>

          <h1>
            Find Work
          </h1>

        </div>

      </header>


      {/* =================================================
          INTRO
      ================================================= */}

      <section className="find-work-intro">

        <div className="find-work-intro-icon">
          <BriefcaseBusiness size={22} />
        </div>

        <div>

          <h2>
            Find your next opportunity
          </h2>

          <p>
            Discover construction work and
            professional opportunities posted
            on Civilink.
          </p>

        </div>

      </section>


      {/* =================================================
          SEARCH
      ================================================= */}

      <div className="find-work-search">

        <Search size={18} />

        <input
          type="text"
          placeholder="Search work, skills or location..."
          value={search}
          onChange={(e) =>
            setSearch(
              e.target.value
            )
          }
        />

        <SlidersHorizontal
          size={17}
        />

      </div>


      {/* =================================================
          FILTERS
      ================================================= */}

      <div className="find-work-filters">

        <button
          type="button"
          className={
            filter === "all"
              ? "work-filter active"
              : "work-filter"
          }
          onClick={() =>
            setFilter("all")
          }
        >
          All work
        </button>


        <button
          type="button"
          className={
            filter === "nearby"
              ? "work-filter active"
              : "work-filter"
          }
          onClick={() =>
            setFilter("nearby")
          }
        >
          Nearby
        </button>


        <button
          type="button"
          className={
            filter === "skills"
              ? "work-filter active"
              : "work-filter"
          }
          onClick={() =>
            setFilter("skills")
          }
        >
          My skills
        </button>

      </div>


      {/* =================================================
          RESULTS
      ================================================= */}

      <div className="find-work-results-heading">

        <div>

          <span>
            OPPORTUNITIES
          </span>

          <h2>
            Available work
          </h2>

        </div>

        <strong>
          {filteredWorkPosts.length}
        </strong>

      </div>


      {/* =================================================
          WORK LIST
      ================================================= */}

      <div className="find-work-list">

        {filteredWorkPosts.length === 0 ? (

          <div className="find-work-empty">

            <BriefcaseBusiness
              size={28}
            />

            <strong>
              No work found
            </strong>

            <p>
              {workPosts.length === 0
                ? "No work opportunities have been posted yet."
                : "Try another search or filter."}
            </p>

          </div>

        ) : (

          filteredWorkPosts.map(
            (work) => {

              const poster =
                work.postedBy || {};


              return (
                <article
                  key={work._id}
                  className="work-opportunity-card"
                >

                  {/* =====================================
                      HEADER
                  ===================================== */}

                  <div className="work-card-top">

                    <div className="work-card-icon">

                      <BriefcaseBusiness
                        size={19}
                      />

                    </div>


                    <div>

                      <h3>
                        {work.title}
                      </h3>

                      {work.workType && (

                        <span className="work-profession">
                          {work.workType}
                        </span>

                      )}

                    </div>

                  </div>


                  {/* =====================================
                      LOCATION / BUDGET
                  ===================================== */}

                  <div className="work-meta">

                    {(work.location?.city ||
                      work.location?.state) && (

                      <span>

                        <MapPin size={14} />

                        {work.location?.city}

                        {work.location?.state
                          ? `, ${work.location.state}`
                          : ""}

                      </span>

                    )}


                    {work.budget > 0 && (

                      <span>

                        <Wallet size={14} />

                        ₹
                        {Number(
                          work.budget
                        ).toLocaleString(
                          "en-IN"
                        )}

                        {work.budgetType
                          ? ` • ${work.budgetType}`
                          : ""}

                      </span>

                    )}

                  </div>


                  {/* =====================================
                      DESCRIPTION
                  ===================================== */}

                  <p className="work-description">
                    {work.description}
                  </p>


                  {/* =====================================
                      SKILLS
                  ===================================== */}

                  {work.skills?.length > 0 && (

                    <div className="find-work-skills">

                      {work.skills
                        .slice(0, 4)
                        .map(
                          (
                            skill,
                            index
                          ) => (

                            <span
                              key={`${skill}-${index}`}
                              className="find-work-skill"
                            >
                              {skill}
                            </span>

                          )
                        )}

                      {work.skills.length > 4 && (

                        <span className="find-work-more">
                          +{work.skills.length - 4}
                        </span>

                      )}

                    </div>

                  )}


                  {/* =====================================
                      POSTER
                  ===================================== */}

                  {poster.name && (

                    <div className="work-poster">

                      <UserRound
                        size={13}
                      />

                      <span>
                        Posted by {poster.name}
                      </span>

                    </div>

                  )}


                  {/* =====================================
                      FOOTER
                  ===================================== */}

                  <div className="work-card-footer">

  <span className="work-posted">

    <Clock size={12} />

    {formatPostedDate(
      work.createdAt
    )}

  </span>


  {poster._id && (
  <button
    type="button"
    className="work-consult-btn"
    onClick={() =>
      openContactProfessional(poster)
    }
  >
    <Phone size={15} />
    Consult
  </button>
)}

</div>

                </article>
              );
            }
          )

        )}

      </div>

      {showConsult && (
  <div
    className="customer-sheet-overlay"
    onClick={() => setShowConsult(false)}
  >
    <div
      className="customer-sheet"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="sheet-handle" />

      <h3 className="sheet-title">
        Contact Professional
      </h3>

      <p className="sheet-subtitle">
        Choose how you'd like to connect.
      </p>

      {consultUser?.phone && (
        <>
          <button
            className="sheet-btn primary"
            onClick={() => {
              window.location.href =
                `tel:${consultUser.phone}`;

              setShowConsult(false);
            }}
          >
            <Phone size={18} />
            <span>Call</span>
          </button>

          <button
            className="sheet-btn primary"
            onClick={() => {
              const text =
                encodeURIComponent(
                  `Hello ${consultUser.name},

I found your work post on Civilink and would like to discuss it with you.`
                );

              window.open(
                `https://wa.me/91${consultUser.phone}?text=${text}`,
                "_blank"
              );

              setShowConsult(false);
            }}
          >
            <MessageCircle size={18} />
            <span>WhatsApp</span>
          </button>
        </>
      )}

      {consultUser?.email && (
        <button
          className="sheet-btn primary"
          onClick={() => {
            window.location.href =
              `mailto:${consultUser.email}?subject=Work Inquiry from Civilink`;

            setShowConsult(false);
          }}
        >
          <Mail size={18} />
          <span>Email</span>
        </button>
      )}

      {!consultUser?.phone &&
        !consultUser?.email && (
          <div className="contact-unavailable">
            Contact information not available.
          </div>
        )}

      <button
        className="sheet-btn outline"
        onClick={() =>
          setShowConsult(false)
        }
      >
        Close
      </button>
    </div>
  </div>
)}

    </div>
  );
}